/**
 * T565 (FR-UI A1.1c) — Period picker with quartal presets.
 *
 * Replaces the two separate dateFrom + dateTo inputs in the
 * expense / invoice filter bars with a single Zeitraum-Button that
 * cycles through:
 *   - Aktuelles Quartal
 *   - YYYY-Q1 / Q2 / Q3 / Q4 (current year + previous year)
 *   - Custom (opens dateFrom + dateTo inputs)
 *   - Alle (clears both dates + the quartal field)
 *
 * The picker is value-controlled — the consumer owns the URL state
 * (search.quartal | search.dateFrom + search.dateTo). On preset
 * select, the picker emits a single onChange with the resolved
 * combination so the consumer's navigate({ search: ... }) call
 * stays atomic.
 */
import { useMemo, useState } from 'react';

export interface PeriodValue {
  /** `YYYY-Q1`/Q2/Q3/Q4 — wins over dateFrom/dateTo when present. */
  quartal?: string;
  dateFrom?: string;
  dateTo?: string;
}

interface Props {
  value: PeriodValue;
  onChange: (next: PeriodValue) => void;
  className?: string;
}

export function computeCurrentQuartal(now: Date = new Date()): string {
  const year = now.getFullYear();
  const q = Math.floor(now.getMonth() / 3) + 1;
  return `${year}-Q${q}`;
}

export function quartalPresets(now: Date = new Date()): string[] {
  const year = now.getFullYear();
  return [
    `${year}-Q1`,
    `${year}-Q2`,
    `${year}-Q3`,
    `${year}-Q4`,
    `${year - 1}-Q1`,
    `${year - 1}-Q2`,
    `${year - 1}-Q3`,
    `${year - 1}-Q4`,
  ];
}

function labelFor(value: PeriodValue): string {
  if (value.quartal) return value.quartal;
  if (value.dateFrom || value.dateTo) {
    return `${value.dateFrom ?? '…'} → ${value.dateTo ?? '…'}`;
  }
  return 'Alle Zeiträume';
}

export function PeriodPicker({ value, onChange, className = '' }: Props) {
  const [open, setOpen] = useState(false);
  const currentQuartal = useMemo(() => computeCurrentQuartal(), []);
  const presets = useMemo(() => quartalPresets(), []);

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="period-picker-trigger"
        className="bg-white/5 border border-white/10 rounded-lg px-3 h-9 text-sm text-white/80 focus:outline-none focus:ring-2 focus:ring-white/20 max-w-[260px] truncate"
      >
        Zeitraum: {labelFor(value)}
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="period-picker-menu"
          className="absolute z-30 mt-1 w-56 max-h-80 overflow-y-auto rounded-lg border border-white/10 bg-zinc-900/95 backdrop-blur-md shadow-xl p-2 text-sm"
        >
          <button
            type="button"
            data-testid="period-preset-current"
            onClick={() => {
              onChange({ quartal: currentQuartal });
              setOpen(false);
            }}
            className="w-full text-left px-2 py-1 rounded hover:bg-white/10 text-emerald-300"
          >
            Aktuelles Quartal ({currentQuartal})
          </button>
          <button
            type="button"
            data-testid="period-preset-all"
            onClick={() => {
              onChange({});
              setOpen(false);
            }}
            className="w-full text-left px-2 py-1 rounded hover:bg-white/10 text-white/60"
          >
            Alle Zeiträume
          </button>
          <div className="border-t border-white/10 my-1" />
          {presets.map((q) => (
            <button
              key={q}
              type="button"
              data-testid={`period-preset-${q}`}
              onClick={() => {
                onChange({ quartal: q });
                setOpen(false);
              }}
              className={`w-full text-left px-2 py-1 rounded hover:bg-white/10 ${
                value.quartal === q ? 'text-white bg-white/10' : 'text-white/70'
              }`}
            >
              {q}
            </button>
          ))}
          <div className="border-t border-white/10 my-1" />
          <div className="px-2 py-1 text-xs text-white/40">Custom</div>
          <div className="flex flex-col gap-1 px-2 pb-2">
            <input
              type="date"
              data-testid="period-custom-from"
              value={value.dateFrom ?? ''}
              onChange={(e) =>
                onChange({ dateFrom: e.target.value || undefined, dateTo: value.dateTo, quartal: undefined })
              }
              className="bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-white"
            />
            <input
              type="date"
              data-testid="period-custom-to"
              value={value.dateTo ?? ''}
              onChange={(e) =>
                onChange({ dateFrom: value.dateFrom, dateTo: e.target.value || undefined, quartal: undefined })
              }
              className="bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-white"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export const __testing = { computeCurrentQuartal, quartalPresets, labelFor };
